import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@monksflow/monks-ui';
import {
  connectorAccountsQueryOptions,
  connectorCampaignsQueryOptions,
  discoveredCampaignsQueryOptions,
  useDismissDiscoveredCampaign,
  useLinkCampaign,
} from '@/entities/media-campaign';
import { track } from '@/shared/analytics';
import { notificationService } from '@/lib/services/notification.service';
import { defaultChannelFor, defaultRoleFor } from '../lib/default-role';
import { PlatformChooser } from './PlatformChooser';

export interface AddCampaignDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
}

type TabValue = 'discovered' | 'browse';

interface SubmitInput {
  connectorId: string;
  externalCampaignId: string;
  name: string;
  platform: string;
  currency: string | null;
  source: TabValue;
}

export function AddCampaignDialog({ projectId, open, onClose }: AddCampaignDialogProps) {
  const { t } = useTranslation('projects');
  const [connectorId, setConnectorId] = useState<string | null>(null);
  const discovered = useQuery(discoveredCampaignsQueryOptions(projectId));
  const connectors = useQuery(connectorAccountsQueryOptions());
  const platformCampaigns = useQuery(connectorCampaignsQueryOptions(connectorId));
  const link = useLinkCampaign(projectId);
  const dismiss = useDismissDiscoveredCampaign(projectId);

  const [chosenTab, setChosenTab] = useState<TabValue | null>(null);
  // Discovery is the default path when there is anything to adopt: it needs no browsing.
  // Controlled rather than `defaultValue`, which Tabs reads once: the discovery query is
  // usually still pending at mount, so the tab would stay on browsing whatever it resolves to.
  const tab = chosenTab ?? ((discovered.data?.length ?? 0) > 0 ? 'discovered' : 'browse');

  const submit = (input: SubmitInput) => {
    const role = defaultRoleFor(input.platform);
    link.mutate(
      {
        projectId,
        connectorId: input.connectorId,
        externalCampaignId: input.externalCampaignId,
        name: input.name,
        channel: defaultChannelFor(input.platform),
        currency: input.currency,
        role,
      },
      {
        onSuccess: (created) => {
          track('performance_campaign_linked', {
            project_id: projectId,
            campaign_id: created.id,
            platform: input.platform,
            role,
            source: input.source,
          });
          onClose();
        },
        onError: (error) => {
          track('performance_campaign_link_failed', {
            project_id: projectId,
            platform: input.platform,
            // eslint-disable-next-line i18next/no-literal-string -- analytics error_code enum, not copy
            error_code: error instanceof Error ? error.name : 'unknown',
          });
          notificationService.showError(
            error instanceof Error
              ? error.message
              : t('performanceCampaignLink.addCampaignDialog.couldNotLinkThatCampaign'),
          );
        },
      },
    );
  };

  const selectedConnector = connectors.data?.find((c) => c.id === connectorId);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('performanceCampaignLink.addCampaignDialog.addACampaign')}</DialogTitle>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(next) => {
            setChosenTab(next as TabValue);
            track('performance_campaign_link_started', {
              project_id: projectId,
              source: next as TabValue,
            });
          }}
        >
          {/* eslint-disable i18next/no-literal-string -- tab identifiers, platform slugs and
              analytics enums below are machine tokens, not copy */}
          <TabsList>
            <TabsTrigger value="discovered">
              {t('performanceCampaignLink.addCampaignDialog.discovered')}
            </TabsTrigger>
            <TabsTrigger value="browse">
              {t('performanceCampaignLink.addCampaignDialog.browseAnAccount')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="discovered">
            <PlatformChooser
              isPending={discovered.isPending}
              isError={discovered.isError}
              isEmpty={(discovered.data?.length ?? 0) === 0}
              onRetry={() => void discovered.refetch()}
              label={t('performanceCampaignLink.addCampaignDialog.discoveredCampaign')}
              errorMessage={
                discovered.error instanceof Error ? discovered.error.message : undefined
              }
            >
              <ul className="flex flex-col gap-2">
                {(discovered.data ?? []).map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-2">
                    <span>{item.externalCampaignName}</span>
                    <span className="flex gap-1">
                      <Button
                        size="sm"
                        onClick={() =>
                          submit({
                            connectorId: item.connectorId,
                            externalCampaignId: item.externalCampaignId,
                            name: item.externalCampaignName,
                            platform: item.platform,
                            // Discovery does not record a currency; the next sync fills it.
                            currency: null,
                            source: 'discovered',
                          })
                        }
                      >
                        {t('performanceCampaignLink.addCampaignDialog.add')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => dismiss.mutate(item.id)}>
                        {t('performanceCampaignLink.addCampaignDialog.dismiss')}
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            </PlatformChooser>
          </TabsContent>

          <TabsContent value="browse">
            <div className="flex flex-col gap-4">
              <PlatformChooser
                isPending={connectors.isPending}
                isError={connectors.isError}
                isEmpty={(connectors.data?.length ?? 0) === 0}
                onRetry={() => void connectors.refetch()}
                label={t('performanceCampaignLink.addCampaignDialog.connector')}
                errorMessage={
                  connectors.error instanceof Error ? connectors.error.message : undefined
                }
              >
                <Select value={connectorId ?? ''} onValueChange={(next) => setConnectorId(next)}>
                  <SelectTrigger
                    aria-label={t('performanceCampaignLink.addCampaignDialog.connector')}
                  >
                    <SelectValue
                      placeholder={t('performanceCampaignLink.addCampaignDialog.chooseAnAccount')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(connectors.data ?? []).map((connector) => (
                      <SelectItem key={connector.id} value={connector.id}>
                        {connector.externalAccountName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </PlatformChooser>

              {connectorId && (
                <PlatformChooser
                  isPending={platformCampaigns.isPending}
                  isError={platformCampaigns.isError}
                  isEmpty={(platformCampaigns.data?.length ?? 0) === 0}
                  onRetry={() => void platformCampaigns.refetch()}
                  label={t('performanceCampaignLink.addCampaignDialog.campaign')}
                  errorMessage={
                    platformCampaigns.error instanceof Error
                      ? platformCampaigns.error.message
                      : undefined
                  }
                >
                  <ul className="flex flex-col gap-2">
                    {(platformCampaigns.data ?? []).map((option) => (
                      <li key={option.externalId} className="flex items-center justify-between">
                        <span>{option.name}</span>
                        <Button
                          size="sm"
                          onClick={() =>
                            submit({
                              connectorId,
                              externalCampaignId: option.externalId,
                              name: option.name,
                              platform: selectedConnector?.platform ?? 'manual',
                              currency: option.currency ?? null,
                              source: 'browse',
                            })
                          }
                        >
                          {t('performanceCampaignLink.addCampaignDialog.add')}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </PlatformChooser>
              )}
            </div>
          </TabsContent>
        </Tabs>
        {/* eslint-enable i18next/no-literal-string */}
      </DialogContent>
    </Dialog>
  );
}
